function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    const detailsElement = event.currentTarget.closest('details');
    const isOpen = !detailsElement.hasAttribute('open');
    event.currentTarget.setAttribute('aria-expanded', isOpen);
    
    // Move focus to first checkbox when details opens
    if (isOpen) {
      setTimeout(() => {
        const firstCheckbox = detailsElement.querySelector('input[type="checkbox"]');
        if (firstCheckbox) {
          firstCheckbox.focus();
        }
      }, 0);
    }
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll('button:not(.localization-selector)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.documentElement.classList.add('menu-drawer-opened');
    document.body.classList.add('menu-drawer-opened');
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.documentElement.classList.remove('menu-drawer-opened');
    document.body.classList.remove('menu-drawer-opened');
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

if (!customElements.get('comp-add-to-cart')) {
  class CompAddToCart extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.button = this.querySelector('button');

      if (this.button) {
        this.button.addEventListener('click', this.addToCart.bind(this));
      }
    }

    addToCart() {
      const { variantId, quantity } = this.button.dataset;
      if (typeof theme !== 'undefined' && typeof theme.cart !== 'undefined') {
        this.button.setAttribute('disabled', true);
        this.button.textContent = 'Adding...'

        theme.cart.addItem(variantId, quantity)
          .then(function(product) {
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {
                product: product,
                addToCartBtn: this.button
              }
            }));
            this.button.textContent = 'Added!'

            setTimeout(() => {
              this.button.removeAttribute('disabled');
              this.button.textContent = 'Add to Cart'
            }, 200);
          }.bind(this))
          .catch((XMLHttpRequest) => {
            this.button.removeAttribute('disabled');
            this.button.textContent = 'Add to Cart'
          });
      }
    }
  }

  customElements.define('comp-add-to-cart', CompAddToCart);
}

if (!customElements.get('comp-see-all-items')) {
  class CompSeeAllItems extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.querySelector('#CartDrawer');
      this.buttons = [...this.querySelectorAll('.btn')];

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => {
          this.drawer.classList.toggle('show-all');
        });
      });
    }
  }

  customElements.define('comp-see-all-items', CompSeeAllItems);
}

if (!customElements.get('comp-cart-bundle-info')) {
  class CompCartBundleInfo extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.button = this.querySelector('button');
      this.buttonTextShow = this.button.querySelector('[data-text-update]');

      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this.classList.toggle('show');
        if (this.classList.contains('show')) {
          this.buttonTextShow.textContent = 'Hide';
        } else {
          this.buttonTextShow.textContent = 'Show';
        }
      });
    }
  }

  customElements.define('comp-cart-bundle-info', CompCartBundleInfo);
}

// Swym SFL line button
(function() {
    function sflInit(swat) {
      swat.SaveForLater.init(
        sfl => {
          swat.sfl = sfl;
          swat.sflLid = sfl.listid;
          document.dispatchEvent(new CustomEvent('swym:sflInited'));
        },
        err => console.error(err)
      )
    }
    if (!window.SwymCallbacks) window.SwymCallbacks = [];
    window.SwymCallbacks.push(sflInit);
})()

if (!customElements.get('swym-sfl-line-button')) {
  class SwymSflLineButton extends HTMLElement {
    constructor() {
      super();
      this.button = null;
      // Bind the method once
      this._addToSFL = this._addToSFL.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('button');
      if(this.button && !this._listenerAdded) {
        this.button.addEventListener('click', this._addToSFL);
        this._listenerAdded = true;
      }
    }

    disconnectedCallback() {
      if (this.button && this._listenerAdded) {
        this.button.removeEventListener('click', this._addToSFL);
        this._listenerAdded = false;
      }
    }

    _addToSFL(evt){
      if (!this.button) return;
      
      const product = [{
        'du': this.button.dataset.productUrl,
        'empi': this.button.dataset.productId,
        'epi': this.button.dataset.variantId,
        'qty': this.button.dataset.quantity
      }]
      
      _swat.SaveForLater.add(_swat.sflLid, product, res => {
        if (location.href.indexOf('/cart') > 0) {
          const saveLaterSection = document.querySelector('section-save-for-later');
          if (saveLaterSection) {
            // Reinit SFL
            saveLaterSection.handleSflInited()
          }
        }
      }, err => console.error(err));

      const cartLine = this.button.closest('[data-cart-item]');
      const cartRemoveButton = cartLine.querySelector('[data-cart-remove-item]');
      cartLine.classList.add('disabled');

      if (cartRemoveButton) {
        cartRemoveButton.click();
      }

      setTimeout(() => {
        cartLine.classList.remove('disabled');
        cartLine.remove();
      }, 500)
    }
  }

  customElements.define('swym-sfl-line-button', SwymSflLineButton);
}

